import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  map,
} from 'lodash';
import {
  Field2 as Field,
  VibeIcon,
  viLock,
  viUnlock,
  color,
} from 'vibeguide';
import Grid from '@mui/material/Grid';
import statesList from '../../../../../utils/states';

function ProfileAddress({
  className = '',
  country = '',
  address1 = '',
  address2 = '',
  city = '',
  state = '',
  postalCode = '',
  geoCoordinatesLocked = true,
  lat = 0,
  long = 0,
  timezone = '',
  locality = {},
  isAdmin = false,
  canEdit = false,
  onUpdate = () => {},
}) {
  const canAdminEdit = isAdmin && canEdit;

  /**
   * When a field value is changed
   */
  const onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    const data = {};
    if (name === 'country' && value === 'US') {
      // reset state field is US is selected as a country
      // ensuring a state from the dropdown is selected
      data.state = '';
    }

    onUpdate({
      [name]: value,
      ...data,
    });
  };

  /**
   * When the coordinates are locked/unlocked
   */
  const onToggleLock = () => {
    onUpdate({
      geoCoordinatesLocked: !geoCoordinatesLocked,
    });
  };

  /**
   * Get the locality label for the location
   */
  const getLocalityLabel = (key) => {
    switch (key) {
      case 'level_0':
        return 'Country';

      case 'level_1':
        return 'State';

      case 'level_2':
        return 'County';

      case 'level_3':
        return 'Township';

      default:
        return 'Unknown';
    }
  };
  let label;
  if (['AT', 'AU', 'BR', 'GE', 'IN', 'MY', 'MX', 'FM', 'MM', 'NZ', 'NG', 'PW', 'SS'].includes(country)) {
    label = 'State';
  } else {
    label = 'Province';
  }
  return (
    <div className={classNames('ProfileAddress', className)}>
      <Grid spacing={2} container>
        <Grid size={12}>
          <Field
            type="text"
            label="Address 1"
            placeholder="123 Main St."
            name="address1"
            value={address1}
            tabIndex={16}
            testId="location-profile-address-1-input"
            onChange={onChange}
            disabled={!canAdminEdit}
            required
          />
        </Grid>

        <Grid size={12}>
          <Field
            type="text"
            label="Address 2"
            placeholder="Optional"
            name="address2"
            value={address2}
            tabIndex={17}
            disabled={!canAdminEdit}
            testId="location-profile-address-2-input"
            onChange={onChange}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="country"
            label="Country"
            name="country"
            value={country}
            tabIndex={18}
            disabled={!canAdminEdit}
            testId="location-profile-country-input"
            onChange={onChange}
            required
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="text"
            label="City"
            placeholder="City"
            name="city"
            value={city}
            tabIndex={19}
            disabled={!canAdminEdit}
            testId="location-profile-city-input"
            onChange={onChange}
            required
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          {country !== 'US' ? (
            <Field
              type="text"
              label={label}
              name="state"
              value={state}
              tabIndex={20}
              disabled={!canAdminEdit}
              testId="location-profile-state-input"
              onChange={onChange}
              required={country === 'CA'}
            />
          ) : (
            <Field
              type="select"
              label="State"
              name="state"
              value={state}
              tabIndex={20}
              options={statesList}
              disabled={!canAdminEdit}
              testId="location-profile-state-select"
              onChange={onChange}
              required
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="text"
            label="Postal Code"
            placeholder="00000"
            name="postalCode"
            value={postalCode}
            tabIndex={21}
            disabled={!canAdminEdit}
            testId="location-profile-postal-code-input"
            onChange={onChange}
            required
          />
        </Grid>

        <Grid size={12}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <div
              style={{
                marginRight: 4,
              }}
            >
              <VibeIcon
                type="button"
                icon={geoCoordinatesLocked
                  ? viLock
                  : viUnlock}
                color={geoCoordinatesLocked
                  ? color.error
                  : color.manatee}
                size={16}
                tooltip={geoCoordinatesLocked
                  ? 'Unlock Coordinates'
                  : 'Lock Coordinates'}
                buttonProps={{
                  size: 32,
                  color: geoCoordinatesLocked
                    ? color.error16
                    : color.lightGray,
                  borderColor: geoCoordinatesLocked
                    ? color.error
                    : color.whiteSmoke,
                  style: {
                    background: 'transparent',
                  },
                }}
                disabled={!canAdminEdit}
                onClick={onToggleLock}
                data-testid="location-profile-geo-coordinates-lock-button"
              />
            </div>

            <div
              style={{
                flexGrow: 1,
                marginRight: 8,
              }}
            >
              <Field
                type="text"
                label="Latitude"
                placeholder="0.00000"
                name="lat"
                value={lat}
                tabIndex={22}
                disabled={!canAdminEdit || geoCoordinatesLocked}
                testId="location-profile-latitude-input"
                onChange={onChange}
              />
            </div>

            <div
              style={{
                flexGrow: 1,
                marginLeft: 8,
              }}
            >
              <Field
                type="text"
                label="Longitude"
                placeholder="0.00000"
                name="long"
                value={long}
                tabIndex={22}
                disabled={!canAdminEdit || geoCoordinatesLocked}
                testId="location-profile-longitude-input"
                onChange={onChange}
              />
            </div>
          </div>
        </Grid>

        <Grid size={6}>
          <Field
            type="custom"
            label="Timezone"
          >
            {timezone}
          </Field>
        </Grid>

        {map(locality, (value, key) => {
          return (
            <Grid key={`locality-${key}`} size={6}>
              <Field
                type="custom"
                label={getLocalityLabel(key)}
              >
                {value}
              </Field>
            </Grid>
          );
        })}

      </Grid>
    </div>
  );
}

ProfileAddress.propTypes = {
  className: PropTypes.string,
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  postalCode: PropTypes.string,
  geoCoordinatesLocked: PropTypes.bool,
  lat: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  long: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  timezone: PropTypes.string,
  locality: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  isAdmin: PropTypes.bool,
  canEdit: PropTypes.bool,
  onUpdate: PropTypes.func,
};

export default ProfileAddress;
